import { setSafeInnerHTML, escapeHtml } from './htmlSecurityModule.js';

const ROLE_LEVEL = Object.freeze({
  user: 0,
  support: 1,
  operator: 2,
  admin: 3,
  super_admin: 4
});

const TAB_KEYS = Object.freeze([
  'overview',
  'members',
  'billing',
  'funnel',
  'risk',
  'alerts',
  'notes',
  'gate',
  'audit',
  'reports'
]);

const RANGE_TO_DAYS = Object.freeze({
  weekly: 7,
  monthly: 30,
  quarterly: 90
});

const NOTE_STATUSES = Object.freeze(['open', 'in_progress', 'resolved']);

let state = null;
let elements = null;
let showToast = () => {};
let switchView = () => {};

let adminDashboardInitialized = false;
let adminReloadSeq = 0;
let selectedUserIds = new Set();
let currentMembers = [];
let currentMemberContext = {
  userId: 0,
  notes: [],
  apiKeys: [],
  lastToken: ''
};

function bindDeps(context = {}) {
  if (context.state) state = context.state;
  if (context.elements) elements = context.elements;
  if (typeof context.showToast === 'function') showToast = context.showToast;
  if (typeof context.switchView === 'function') switchView = context.switchView;
}

function ensureAdminStateShape() {
  if (!state.admin || typeof state.admin !== 'object') {
    state.admin = {
      query: '',
      planCode: 'all',
      status: 'all',
      range: 'monthly',
      activeTab: 'overview',
      page: 1,
      pageSize: 20,
      total: 0,
      selectedUserId: null
    };
    return;
  }

  const defaults = {
    query: '',
    planCode: 'all',
    status: 'all',
    range: 'monthly',
    activeTab: 'overview',
    page: 1,
    pageSize: 20,
    total: 0,
    selectedUserId: null
  };
  state.admin = { ...defaults, ...state.admin };

  if (!TAB_KEYS.includes(String(state.admin.activeTab || '').toLowerCase())) {
    state.admin.activeTab = 'overview';
  }
  if (!['weekly', 'monthly', 'quarterly'].includes(String(state.admin.range || '').toLowerCase())) {
    state.admin.range = 'monthly';
  }
}

function getCurrentRole() {
  return String(state?.auth?.user?.role || 'user').toLowerCase();
}

function getRoleLevel(role) {
  return ROLE_LEVEL[String(role || 'user').toLowerCase()] ?? 0;
}

function isStaffUser() {
  return getRoleLevel(getCurrentRole()) >= ROLE_LEVEL.support;
}

function canOperateNotes() {
  return getRoleLevel(getCurrentRole()) >= ROLE_LEVEL.operator;
}

function canMutateMembers() {
  return getRoleLevel(getCurrentRole()) >= ROLE_LEVEL.admin;
}

function canUpdateRoles() {
  return getRoleLevel(getCurrentRole()) >= ROLE_LEVEL.super_admin;
}

function setLoadingState(loading) {
  if (!elements.adminRefreshBtn) return;
  elements.adminRefreshBtn.disabled = !!loading;
  elements.adminRefreshBtn.setAttribute('aria-busy', loading ? 'true' : 'false');
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, {
    credentials: 'include',
    ...options
  });

  let payload = {};
  try {
    payload = await response.json();
  } catch {
  }

  if (!response.ok) {
    const error = new Error(payload?.error || '请求失败');
    error.status = response.status;
    throw error;
  }

  return payload;
}

async function requestBlob(url, options = {}) {
  const response = await fetch(url, {
    credentials: 'include',
    ...options
  });

  if (!response.ok) {
    let payload = {};
    try {
      payload = await response.json();
    } catch {
    }
    const error = new Error(payload?.error || '下载失败');
    error.status = response.status;
    throw error;
  }

  const blob = await response.blob();
  return {
    blob,
    disposition: String(response.headers.get('Content-Disposition') || '')
  };
}

function downloadBlobFile(blob, disposition, fallbackName = 'report.csv') {
  if (typeof document === 'undefined') return;
  const filenameMatch = disposition.match(/filename\s*=\s*"?([^";]+)"?/i);
  const filename = filenameMatch?.[1] || fallbackName;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('zh-CN', {
    hour12: false,
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatPercent(value) {
  const num = Number(value || 0);
  return `${(num * 100).toFixed(1)}%`;
}

function formatMrr(cents) {
  const value = Number(cents || 0);
  return `¥${(value / 100).toFixed(2)}`;
}

function formatBoolean(value) {
  return value ? '是' : '否';
}

function escapeJson(value) {
  try {
    return escapeHtml(JSON.stringify(value || {}, null, 2));
  } catch {
    return '{}';
  }
}

function roleBadgeClass(role) {
  const normalized = String(role || 'user').toLowerCase();
  if (normalized === 'super_admin' || normalized === 'admin') return 'is-admin';
  if (normalized === 'operator') return 'is-operator';
  if (normalized === 'support') return 'is-support';
  return '';
}

function statusBadgeClass(status) {
  return String(status || '').toLowerCase() === 'suspended' ? 'is-suspended' : 'is-active';
}

function normalizeScopesInput(text) {
  const tokens = String(text || '')
    .split(',')
    .map((item) => String(item || '').trim().toLowerCase())
    .filter(Boolean);
  if (!tokens.length) return ['reader'];
  return Array.from(new Set(tokens));
}

function getActiveTab() {
  ensureAdminStateShape();
  const tab = String(state.admin.activeTab || 'overview').toLowerCase();
  return TAB_KEYS.includes(tab) ? tab : 'overview';
}

function readFiltersFromUi() {
  ensureAdminStateShape();
  state.admin.query = String(elements.adminSearchInput?.value || '').trim();
  state.admin.planCode = String(elements.adminPlanFilter?.value || 'all').toLowerCase();
  state.admin.status = String(elements.adminStatusFilter?.value || 'all').toLowerCase();
  state.admin.range = String(elements.adminRangeFilter?.value || state.admin.range || 'monthly').toLowerCase();
}

function syncFiltersToUi() {
  ensureAdminStateShape();
  if (elements.adminSearchInput) elements.adminSearchInput.value = state.admin.query || '';
  if (elements.adminPlanFilter) elements.adminPlanFilter.value = state.admin.planCode || 'all';
  if (elements.adminStatusFilter) elements.adminStatusFilter.value = state.admin.status || 'all';
  if (elements.adminRangeFilter) elements.adminRangeFilter.value = state.admin.range || 'monthly';
  if (elements.adminReportsRangeFilter) elements.adminReportsRangeFilter.value = state.admin.range || 'monthly';
}

function setActiveTab(tab) {
  ensureAdminStateShape();
  const normalized = String(tab || '').toLowerCase();
  if (!TAB_KEYS.includes(normalized)) return;
  state.admin.activeTab = normalized;
  state.admin.page = 1;

  elements.adminTabItems?.forEach((item) => {
    const isActive = item?.dataset?.adminTab === normalized;
    item.classList.toggle('active', isActive);
  });

  elements.adminPanels?.forEach((panel) => {
    const isActive = panel?.dataset?.adminPanel === normalized;
    panel.classList.toggle('active', isActive);
  });
}

function getDaysFromRange() {
  const range = String(state?.admin?.range || 'monthly').toLowerCase();
  return RANGE_TO_DAYS[range] || RANGE_TO_DAYS.monthly;
}

function renderNoPermissionHint() {
  renderOverview({
    totalUsers: '--',
    activePaidUsers: '--',
    suspendedUsers: '--',
    monthlyRecurringRevenueCents: 0,
    expiringSoonUsers: '--',
    autoRenewUsers: '--'
  });

  if (elements.adminMembersTableBody) {
    setSafeInnerHTML(elements.adminMembersTableBody, '', { profile: 'app' });
  }
  if (elements.adminMembersEmpty) {
    elements.adminMembersEmpty.style.display = '';
    elements.adminMembersEmpty.textContent = '仅支持/运营/管理员可访问该页面';
  }
}

function renderOverview(overview = {}) {
  if (elements.adminStatTotalUsers) {
    elements.adminStatTotalUsers.textContent = String(overview.totalUsers ?? 0);
  }
  if (elements.adminStatActiveMembers) {
    elements.adminStatActiveMembers.textContent = String(overview.activePaidUsers ?? 0);
  }
  if (elements.adminStatSuspendedMembers) {
    elements.adminStatSuspendedMembers.textContent = String(overview.suspendedUsers ?? 0);
  }
  if (elements.adminStatMrr) {
    elements.adminStatMrr.textContent = formatMrr(overview.monthlyRecurringRevenueCents);
  }

  if (elements.adminOverviewExtra) {
    const html = `
      <article class="admin-mini-card">
        <div class="admin-mini-label">7天内到期</div>
        <div class="admin-mini-value">${escapeHtml(String(overview.expiringSoonUsers ?? 0))}</div>
      </article>
      <article class="admin-mini-card">
        <div class="admin-mini-label">自动续费用户</div>
        <div class="admin-mini-value">${escapeHtml(String(overview.autoRenewUsers ?? 0))}</div>
      </article>
    `;
    setSafeInnerHTML(elements.adminOverviewExtra, html, { profile: 'app' });
  }
}

function updateBatchToolbar() {
  if (!elements.adminBatchToolbar || !elements.adminBatchCount) return;
  const selectedCount = selectedUserIds.size;
  elements.adminBatchToolbar.classList.toggle('active', selectedCount > 0);
  elements.adminBatchCount.textContent = `已选 ${selectedCount} 人`;
  if (elements.adminSelectAllRows) {
    const allCount = currentMembers.length;
    elements.adminSelectAllRows.checked = allCount > 0 && selectedCount === allCount;
    elements.adminSelectAllRows.indeterminate = selectedCount > 0 && selectedCount < allCount;
  }
}

function renderMembers(items = []) {
  currentMembers = Array.isArray(items) ? items : [];
  const tbody = elements.adminMembersTableBody;
  if (!tbody) return;

  const nextSelected = new Set();
  currentMembers.forEach((item) => {
    const userId = Number(item?.userId || 0);
    if (selectedUserIds.has(userId)) nextSelected.add(userId);
  });
  selectedUserIds = nextSelected;

  if (!currentMembers.length) {
    setSafeInnerHTML(tbody, '', { profile: 'app' });
    if (elements.adminMembersEmpty) {
      elements.adminMembersEmpty.style.display = '';
      elements.adminMembersEmpty.textContent = '暂无匹配用户';
    }
    updateBatchToolbar();
    renderMemberContext(null);
    return;
  }

  const canRoleEdit = canUpdateRoles();
  const canMemberEdit = canMutateMembers();
  const rowsHtml = currentMembers.map((item) => {
    const userId = Number(item?.userId || 0);
    const planCode = String(item?.planCode || 'free').toLowerCase();
    const status = String(item?.membershipStatus || 'active').toLowerCase();
    const role = String(item?.role || 'user').toLowerCase();
    const isSelected = selectedUserIds.has(userId);

    return `
      <tr data-user-id="${userId}">
        <td>
          <input type="checkbox" class="admin-row-check" data-member-check="1" ${isSelected ? 'checked' : ''}>
        </td>
        <td data-label="用户">
          <div class="admin-member-main">${escapeHtml(item?.nickname || '-')}</div>
          <div class="admin-member-sub">${escapeHtml(item?.email || '-')}</div>
        </td>
        <td data-label="角色">
          <span class="admin-role-tag ${roleBadgeClass(role)}">${escapeHtml(role)}</span>
          <select class="admin-row-select" data-field="role" ${canRoleEdit ? '' : 'disabled'}>
            <option value="user" ${role === 'user' ? 'selected' : ''}>user</option>
            <option value="support" ${role === 'support' ? 'selected' : ''}>support</option>
            <option value="operator" ${role === 'operator' ? 'selected' : ''}>operator</option>
            <option value="admin" ${role === 'admin' ? 'selected' : ''}>admin</option>
            <option value="super_admin" ${role === 'super_admin' ? 'selected' : ''}>super_admin</option>
          </select>
        </td>
        <td data-label="档位">
          <select class="admin-row-select" data-field="plan" ${canMemberEdit ? '' : 'disabled'}>
            <option value="free" ${planCode === 'free' ? 'selected' : ''}>Free</option>
            <option value="pro" ${planCode === 'pro' ? 'selected' : ''}>Pro</option>
            <option value="max" ${planCode === 'max' ? 'selected' : ''}>Max</option>
          </select>
        </td>
        <td data-label="状态">
          <span class="admin-status-tag ${statusBadgeClass(status)}">${escapeHtml(status)}</span>
          <select class="admin-row-select" data-field="status" ${canMemberEdit ? '' : 'disabled'}>
            <option value="active" ${status === 'active' ? 'selected' : ''}>Active</option>
            <option value="suspended" ${status === 'suspended' ? 'selected' : ''}>Suspended</option>
          </select>
        </td>
        <td data-label="续费">
          <label class="admin-boolean-wrap">
            <input type="checkbox" data-field="autoRenew" ${item?.autoRenew ? 'checked' : ''} ${canMemberEdit ? '' : 'disabled'}>
            <span>${formatBoolean(!!item?.autoRenew)}</span>
          </label>
        </td>
        <td data-label="到期">${escapeHtml(formatDateTime(item?.expiresAt))}</td>
        <td data-label="最近登录">${escapeHtml(formatDateTime(item?.lastLoginAt))}</td>
        <td data-label="操作">
          <div class="admin-row-actions">
            <button class="admin-row-save" data-admin-save="1" type="button" ${canMemberEdit || canRoleEdit ? '' : 'disabled'}>保存</button>
            <button class="admin-row-detail" data-admin-member-detail="1" type="button">详情</button>
          </div>
        </td>
      </tr>
    `;
  }).join('');

  setSafeInnerHTML(tbody, rowsHtml, { profile: 'app' });
  if (elements.adminMembersEmpty) {
    elements.adminMembersEmpty.style.display = 'none';
    elements.adminMembersEmpty.textContent = '';
  }
  updateBatchToolbar();

  if (state.admin.selectedUserId) {
    const stillExists = currentMembers.some((item) => Number(item?.userId || 0) === Number(state.admin.selectedUserId));
    if (!stillExists) {
      state.admin.selectedUserId = null;
      renderMemberContext(null);
    }
  }
}

function renderMemberContext(member) {
  if (!elements.adminMemberContext) return;
  if (!member) {
    setSafeInnerHTML(elements.adminMemberContext, '', { profile: 'app' });
    return;
  }

  const notesHtml = Array.isArray(currentMemberContext.notes) && currentMemberContext.notes.length
    ? currentMemberContext.notes.map((item) => `
      <div class="admin-context-item" data-note-id="${Number(item?.id || 0)}">
        <div class="admin-context-title">#${Number(item?.id || 0)} · ${escapeHtml(String(item?.status || 'open'))}</div>
        <div class="admin-context-sub">${escapeHtml(String(item?.note || ''))}</div>
        <div class="admin-context-meta">更新：${escapeHtml(formatDateTime(item?.updatedAt))}</div>
      </div>
    `).join('')
    : '<div class="admin-empty-inline">暂无工单</div>';

  const keysHtml = Array.isArray(currentMemberContext.apiKeys) && currentMemberContext.apiKeys.length
    ? currentMemberContext.apiKeys.map((item) => `
      <div class="admin-context-item" data-api-key-id="${Number(item?.id || 0)}">
        <div class="admin-context-title">${escapeHtml(String(item?.name || '未命名'))}</div>
        <div class="admin-context-sub">${escapeHtml(String(item?.keyPrefix || ''))} · ${escapeHtml(String(item?.status || 'active'))}</div>
        <div class="admin-context-meta">Scopes: ${escapeHtml(Array.isArray(item?.scopes) ? item.scopes.join(', ') : 'reader')}</div>
        <button class="admin-mini-btn" type="button" data-admin-revoke-key="1" ${canMutateMembers() ? '' : 'disabled'}>吊销</button>
      </div>
    `).join('')
    : '<div class="admin-empty-inline">暂无 API Key</div>';

  const lastTokenHtml = currentMemberContext.lastToken
    ? `<div class="admin-token-box">新密钥（仅展示一次）：<code>${escapeHtml(currentMemberContext.lastToken)}</code></div>`
    : '';

  const html = `
    <div class="admin-context-head">
      <h3>用户详情</h3>
      <div>${escapeHtml(String(member?.email || '-'))}</div>
    </div>
    <div class="admin-grid-two">
      <article class="admin-context-card">
        <h4>新增工单</h4>
        <textarea id="adminContextNoteInput" class="admin-context-textarea" placeholder="记录用户问题 / 跟进备注"></textarea>
        <div class="admin-inline-actions">
          <select class="glass-btn admin-filter-select" id="adminContextNoteStatus">
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
          <button class="glass-btn" type="button" id="adminContextCreateNoteBtn" ${canOperateNotes() ? '' : 'disabled'}>创建工单</button>
        </div>
        <div class="admin-context-list">${notesHtml}</div>
      </article>

      <article class="admin-context-card">
        <h4>API Key</h4>
        <input id="adminContextKeyName" class="admin-context-input" type="text" placeholder="密钥名称" maxlength="64">
        <input id="adminContextKeyScopes" class="admin-context-input" type="text" placeholder="scopes，逗号分隔：reader,music,ai">
        <div class="admin-inline-actions">
          <button class="glass-btn" type="button" id="adminContextCreateKeyBtn" ${canMutateMembers() ? '' : 'disabled'}>创建密钥</button>
        </div>
        ${lastTokenHtml}
        <div class="admin-context-list">${keysHtml}</div>
      </article>
    </div>
  `;

  setSafeInnerHTML(elements.adminMemberContext, html, { profile: 'app' });
}

function renderBilling(overview = {}) {
  const byPlan = Array.isArray(overview?.byPlan) ? overview.byPlan : [];
  if (elements.adminBillingCards) {
    const html = `
      <article class="admin-mini-card">
        <div class="admin-mini-label">7天内到期</div>
        <div class="admin-mini-value">${escapeHtml(String(overview?.expiringSoonUsers ?? 0))}</div>
      </article>
      <article class="admin-mini-card">
        <div class="admin-mini-label">自动续费失败</div>
        <div class="admin-mini-value">${escapeHtml(String(overview?.overdueAutoRenewUsers ?? 0))}</div>
      </article>
    `;
    setSafeInnerHTML(elements.adminBillingCards, html, { profile: 'app' });
  }

  if (!elements.adminBillingTableBody) return;
  if (!byPlan.length) {
    setSafeInnerHTML(elements.adminBillingTableBody, '', { profile: 'app' });
    if (elements.adminBillingEmpty) {
      elements.adminBillingEmpty.style.display = '';
      elements.adminBillingEmpty.textContent = '暂无账单数据';
    }
    return;
  }

  const html = byPlan.map((item) => `
    <tr>
      <td>${escapeHtml(String(item?.planName || item?.planCode || '-'))}</td>
      <td>${escapeHtml(String(item?.users ?? 0))}</td>
      <td>${escapeHtml(formatMrr(item?.mrrCents))}</td>
    </tr>
  `).join('');
  setSafeInnerHTML(elements.adminBillingTableBody, html, { profile: 'app' });
  if (elements.adminBillingEmpty) {
    elements.adminBillingEmpty.style.display = 'none';
    elements.adminBillingEmpty.textContent = '';
  }
}

function renderFunnel(funnel = {}) {
  if (!elements.adminFunnelCards || !elements.adminFunnelList) return;

  const cardHtml = `
    <article class="admin-mini-card">
      <div class="admin-mini-label">付费转化率</div>
      <div class="admin-mini-value">${escapeHtml(formatPercent(funnel?.paidConversionRate || 0))}</div>
    </article>
    <article class="admin-mini-card">
      <div class="admin-mini-label">统计起点</div>
      <div class="admin-mini-value">${escapeHtml(formatDateTime(funnel?.since))}</div>
    </article>
  `;
  setSafeInnerHTML(elements.adminFunnelCards, cardHtml, { profile: 'app' });

  const steps = [
    { key: 'registered', label: '注册' },
    { key: 'activated', label: '激活' },
    { key: 'paid', label: '付费' },
    { key: 'renewed', label: '续费开启' },
    { key: 'churned', label: '流失/到期' }
  ];
  const max = Math.max(1, ...steps.map((item) => Number(funnel?.[item.key] || 0)));
  const listHtml = steps.map((item) => {
    const value = Number(funnel?.[item.key] || 0);
    const width = Math.max(3, Math.round((value / max) * 100));
    return `
      <div class="admin-funnel-item">
        <div class="admin-funnel-title">${escapeHtml(item.label)}</div>
        <div class="admin-funnel-bar"><span style="width: ${width}%"></span></div>
        <div class="admin-funnel-value">${escapeHtml(String(value))}</div>
      </div>
    `;
  }).join('');

  setSafeInnerHTML(elements.adminFunnelList, listHtml, { profile: 'app' });
}

function renderRisk(flags = []) {
  if (!elements.adminRiskTableBody) return;
  if (!Array.isArray(flags) || !flags.length) {
    setSafeInnerHTML(elements.adminRiskTableBody, '', { profile: 'app' });
    if (elements.adminRiskEmpty) {
      elements.adminRiskEmpty.style.display = '';
      elements.adminRiskEmpty.textContent = '暂无风险标记';
    }
    return;
  }

  const html = flags.map((item) => {
    const type = String(item?.type || 'unknown');
    const user = item?.email || (item?.userId ? `#${item.userId}` : '-');
    const details = type === 'failed_login_spike'
      ? `24h失败次数: ${Number(item?.count || 0)}`
      : `IP:${Number(item?.ipCount || 0)} / UA:${Number(item?.uaCount || 0)} / 登录:${Number(item?.loginCount || 0)}`;
    const time = item?.lastAt || null;
    return `
      <tr>
        <td>${escapeHtml(type)}</td>
        <td>${escapeHtml(String(user))}</td>
        <td>${escapeHtml(details)}</td>
        <td>${escapeHtml(formatDateTime(time))}</td>
      </tr>
    `;
  }).join('');

  setSafeInnerHTML(elements.adminRiskTableBody, html, { profile: 'app' });
  if (elements.adminRiskEmpty) {
    elements.adminRiskEmpty.style.display = 'none';
    elements.adminRiskEmpty.textContent = '';
  }
}

function renderAlerts(alerts = []) {
  if (!elements.adminAlertsTableBody) return;
  if (!Array.isArray(alerts) || !alerts.length) {
    setSafeInnerHTML(elements.adminAlertsTableBody, '', { profile: 'app' });
    if (elements.adminAlertsEmpty) {
      elements.adminAlertsEmpty.style.display = '';
      elements.adminAlertsEmpty.textContent = '暂无告警';
    }
    return;
  }

  const html = alerts.map((item) => `
    <tr>
      <td>${escapeHtml(String(item?.type || 'unknown'))}</td>
      <td>
        <div class="admin-member-main">${escapeHtml(String(item?.nickname || '-'))}</div>
        <div class="admin-member-sub">${escapeHtml(String(item?.email || '-'))}</div>
      </td>
      <td>${escapeHtml(String(item?.planCode || 'free'))}</td>
      <td>${escapeHtml(formatDateTime(item?.expiresAt))}</td>
      <td>${escapeHtml(formatBoolean(!!item?.autoRenew))}</td>
    </tr>
  `).join('');

  setSafeInnerHTML(elements.adminAlertsTableBody, html, { profile: 'app' });
  if (elements.adminAlertsEmpty) {
    elements.adminAlertsEmpty.style.display = 'none';
    elements.adminAlertsEmpty.textContent = '';
  }
}

function renderNotes(items = []) {
  if (!elements.adminNotesTableBody) return;
  if (!Array.isArray(items) || !items.length) {
    setSafeInnerHTML(elements.adminNotesTableBody, '', { profile: 'app' });
    if (elements.adminNotesEmpty) {
      elements.adminNotesEmpty.style.display = '';
      elements.adminNotesEmpty.textContent = '暂无工单';
    }
    return;
  }

  const canUpdate = canOperateNotes();
  const html = items.map((item) => {
    const status = String(item?.status || 'open');
    const attachments = Array.isArray(item?.attachments) ? item.attachments : [];
    const sourceLabel = String(item?.source || '') === 'self_service'
      ? `用户自助(${String(item?.createdByRole || 'user')})`
      : `后台录入(${String(item?.createdByRole || 'staff')})`;
    return `
      <tr data-note-id="${Number(item?.id || 0)}">
        <td>#${Number(item?.id || 0)}</td>
        <td>
          <div class="admin-member-main">#${Number(item?.userId || 0)}</div>
          <div class="admin-member-sub">${escapeHtml(String(item?.userEmail || '-'))}</div>
        </td>
        <td>
          <div class="admin-member-main">${escapeHtml(sourceLabel)}</div>
          <div class="admin-member-sub">${escapeHtml(String(item?.createdByEmail || '-'))}</div>
        </td>
        <td>
          <select class="admin-row-select" data-note-status="1" ${canUpdate ? '' : 'disabled'}>
            <option value="open" ${status === 'open' ? 'selected' : ''}>Open</option>
            <option value="in_progress" ${status === 'in_progress' ? 'selected' : ''}>In Progress</option>
            <option value="resolved" ${status === 'resolved' ? 'selected' : ''}>Resolved</option>
          </select>
        </td>
        <td>${escapeHtml(String(item?.note || '-'))}</td>
        <td>${escapeHtml(attachments.length ? attachments.join(', ') : '-')}</td>
        <td>${escapeHtml(String(item?.assigneeEmail || '-'))}</td>
        <td>${escapeHtml(formatDateTime(item?.updatedAt))}</td>
        <td>
          <button class="admin-row-save" type="button" data-admin-note-save="1" ${canUpdate ? '' : 'disabled'}>更新</button>
        </td>
      </tr>
    `;
  }).join('');

  setSafeInnerHTML(elements.adminNotesTableBody, html, { profile: 'app' });
  if (elements.adminNotesEmpty) {
    elements.adminNotesEmpty.style.display = 'none';
    elements.adminNotesEmpty.textContent = '';
  }
}

function renderGateRules(rules = []) {
  if (!elements.adminGateRulesTableBody) return;
  if (!Array.isArray(rules) || !rules.length) {
    setSafeInnerHTML(elements.adminGateRulesTableBody, '', { profile: 'app' });
    if (elements.adminGateEmpty) {
      elements.adminGateEmpty.style.display = '';
      elements.adminGateEmpty.textContent = '暂无门禁规则';
    }
    return;
  }

  const canEdit = canMutateMembers();
  const html = rules.map((item) => `
    <tr data-plan-code="${escapeHtml(String(item?.planCode || 'free'))}">
      <td>${escapeHtml(String(item?.planCode || 'free').toUpperCase())}</td>
      <td><input class="admin-rule-input" data-field="deviceLimit" type="number" min="1" max="100" value="${Number(item?.deviceLimit || 1)}" ${canEdit ? '' : 'disabled'}></td>
      <td><input class="admin-rule-input" data-field="concurrentLimit" type="number" min="1" max="100" value="${Number(item?.concurrentLimit || 1)}" ${canEdit ? '' : 'disabled'}></td>
      <td><input class="admin-rule-input" data-field="scopes" type="text" value="${escapeHtml(Array.isArray(item?.scopes) ? item.scopes.join(',') : 'reader')}" ${canEdit ? '' : 'disabled'}></td>
      <td>${escapeHtml(formatDateTime(item?.updatedAt))}</td>
      <td><button class="admin-row-save" type="button" data-admin-gate-save="1" ${canEdit ? '' : 'disabled'}>保存</button></td>
    </tr>
  `).join('');

  setSafeInnerHTML(elements.adminGateRulesTableBody, html, { profile: 'app' });
  if (elements.adminGateEmpty) {
    elements.adminGateEmpty.style.display = 'none';
    elements.adminGateEmpty.textContent = '';
  }
}

function renderAudit(items = []) {
  if (!elements.adminAuditTableBody) return;
  if (!Array.isArray(items) || !items.length) {
    setSafeInnerHTML(elements.adminAuditTableBody, '', { profile: 'app' });
    if (elements.adminAuditEmpty) {
      elements.adminAuditEmpty.style.display = '';
      elements.adminAuditEmpty.textContent = '暂无审计日志';
    }
    return;
  }

  const html = items.map((item) => `
    <tr>
      <td>${escapeHtml(formatDateTime(item?.createdAt))}</td>
      <td>${escapeHtml(String(item?.action || '-'))}</td>
      <td>${escapeHtml(String(item?.actorEmail || '-'))}</td>
      <td>${escapeHtml(String(item?.targetEmail || '-'))}</td>
      <td>
        <div class="admin-json-pre">from: ${escapeJson(item?.from)}</div>
        <div class="admin-json-pre">to: ${escapeJson(item?.to)}</div>
      </td>
    </tr>
  `).join('');

  setSafeInnerHTML(elements.adminAuditTableBody, html, { profile: 'app' });
  if (elements.adminAuditEmpty) {
    elements.adminAuditEmpty.style.display = 'none';
    elements.adminAuditEmpty.textContent = '';
  }
}

function renderReports(report = {}) {
  if (elements.adminReportNewUsers) {
    elements.adminReportNewUsers.textContent = String(report?.newUsers ?? 0);
  }
  if (elements.adminReportActivatedMemberships) {
    elements.adminReportActivatedMemberships.textContent = String(report?.activatedMemberships ?? 0);
  }
  if (elements.adminReportAdminOperations) {
    elements.adminReportAdminOperations.textContent = String(report?.adminOperations ?? 0);
  }
  if (elements.adminReportCurrentMrr) {
    elements.adminReportCurrentMrr.textContent = formatMrr(report?.currentMrrCents || 0);
  }

  if (elements.adminReportsMeta) {
    const html = `
      <article class="admin-mini-card">
        <div class="admin-mini-label">统计范围</div>
        <div class="admin-mini-value">${escapeHtml(String(report?.range || state?.admin?.range || 'monthly'))}</div>
      </article>
      <article class="admin-mini-card">
        <div class="admin-mini-label">统计起点</div>
        <div class="admin-mini-value">${escapeHtml(formatDateTime(report?.since))}</div>
      </article>
    `;
    setSafeInnerHTML(elements.adminReportsMeta, html, { profile: 'app' });
  }
}

async function fetchOverview() {
  const payload = await requestJson('/api/admin/overview');
  return payload?.overview || {};
}

async function fetchMembers() {
  ensureAdminStateShape();
  const query = new URLSearchParams({
    search: state.admin.query || '',
    planCode: state.admin.planCode || 'all',
    status: state.admin.status || 'all',
    page: String(state.admin.page || 1),
    pageSize: String(state.admin.pageSize || 20)
  });
  const payload = await requestJson(`/api/admin/members?${query.toString()}`);
  state.admin.total = Number(payload?.total || 0);
  return Array.isArray(payload?.items) ? payload.items : [];
}

async function fetchBillingOverview() {
  const payload = await requestJson('/api/admin/billing/overview');
  return payload?.overview || {};
}

async function fetchFunnel() {
  const range = String(state?.admin?.range || 'monthly').toLowerCase();
  const payload = await requestJson(`/api/admin/analytics/funnel?range=${encodeURIComponent(range)}`);
  return payload?.funnel || {};
}

async function fetchRiskFlags() {
  const payload = await requestJson('/api/admin/risk-flags');
  return Array.isArray(payload?.flags) ? payload.flags : [];
}

async function fetchAlerts() {
  const days = getDaysFromRange();
  const payload = await requestJson(`/api/admin/alerts?days=${days}`);
  return Array.isArray(payload?.alerts) ? payload.alerts : [];
}

async function fetchNotes() {
  const status = String(elements.adminNotesStatusFilter?.value || 'all').toLowerCase();
  const query = new URLSearchParams({ status, page: '1', pageSize: '50' });
  const payload = await requestJson(`/api/admin/notes?${query.toString()}`);
  return Array.isArray(payload?.items) ? payload.items : [];
}

async function fetchGateRules() {
  const payload = await requestJson('/api/admin/gate/rules');
  return Array.isArray(payload?.rules) ? payload.rules : [];
}

async function fetchAuditLogs() {
  const action = String(elements.adminAuditActionFilter?.value || 'all').toLowerCase();
  const search = String(state?.admin?.query || '').trim().toLowerCase();
  const query = new URLSearchParams({
    action,
    search,
    page: '1',
    pageSize: '50'
  });
  const payload = await requestJson(`/api/admin/audit-logs?${query.toString()}`);
  return Array.isArray(payload?.items) ? payload.items : [];
}

async function fetchReportSummary() {
  const range = String(elements.adminReportsRangeFilter?.value || state?.admin?.range || 'monthly').toLowerCase();
  state.admin.range = range;
  if (elements.adminRangeFilter) elements.adminRangeFilter.value = range;
  const payload = await requestJson(`/api/admin/reports/summary?range=${encodeURIComponent(range)}`);
  return payload?.report || {};
}

function getMemberById(userId) {
  return currentMembers.find((item) => Number(item?.userId || 0) === Number(userId)) || null;
}

async function reloadMemberContext(userId) {
  const member = getMemberById(userId);
  if (!member) {
    renderMemberContext(null);
    return;
  }

  const [notesPayload, keysPayload] = await Promise.all([
    requestJson(`/api/admin/members/${userId}/notes`),
    requestJson(`/api/admin/members/${userId}/api-keys`)
  ]);

  currentMemberContext = {
    ...currentMemberContext,
    userId,
    notes: Array.isArray(notesPayload?.notes) ? notesPayload.notes : [],
    apiKeys: Array.isArray(keysPayload?.items) ? keysPayload.items : []
  };
  state.admin.selectedUserId = userId;
  renderMemberContext(member);
}

async function saveMemberRow(rowEl, saveBtn) {
  if (!rowEl || !saveBtn) return;
  const userId = Number(rowEl?.dataset?.userId || 0);
  if (!userId) return;

  const planSelect = rowEl.querySelector('[data-field="plan"]');
  const statusSelect = rowEl.querySelector('[data-field="status"]');
  const roleSelect = rowEl.querySelector('[data-field="role"]');
  const autoRenewInput = rowEl.querySelector('[data-field="autoRenew"]');

  const planCode = String(planSelect?.value || 'free').toLowerCase();
  const status = String(statusSelect?.value || 'active').toLowerCase();
  const role = String(roleSelect?.value || '').toLowerCase();
  const autoRenew = !!autoRenewInput?.checked;

  saveBtn.disabled = true;
  try {
    if (canMutateMembers()) {
      await requestJson(`/api/admin/members/${userId}/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planCode, autoRenew })
      });

      await requestJson(`/api/admin/members/${userId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
    }

    if (canUpdateRoles() && role) {
      await requestJson(`/api/admin/members/${userId}/role`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role })
      });
    }

    showToast('会员信息已更新');
    await reloadAdminDashboard();
  } catch (error) {
    showToast(error?.message || '保存失败');
  } finally {
    saveBtn.disabled = false;
  }
}

async function applyBatchUpdate() {
  const userIds = Array.from(selectedUserIds.values());
  if (!userIds.length) {
    showToast('请先选择用户');
    return;
  }
  if (!canMutateMembers()) {
    showToast('当前角色无批量修改权限');
    return;
  }

  const planCode = String(elements.adminBatchPlanSelect?.value || '').toLowerCase();
  const status = String(elements.adminBatchStatusSelect?.value || '').toLowerCase();
  const autoRenewRaw = String(elements.adminBatchAutoRenewSelect?.value || '').toLowerCase();

  const payload = { userIds };
  if (planCode) payload.planCode = planCode;
  if (status) payload.status = status;
  if (autoRenewRaw === 'true') payload.autoRenew = true;
  if (autoRenewRaw === 'false') payload.autoRenew = false;

  if (!payload.planCode && !payload.status && typeof payload.autoRenew === 'undefined') {
    showToast('请选择至少一种批量变更项');
    return;
  }

  try {
    const result = await requestJson('/api/admin/members/batch-update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    showToast(`批量完成：${Number(result?.changed || 0)}/${userIds.length}`);
    selectedUserIds = new Set();
    if (elements.adminBatchPlanSelect) elements.adminBatchPlanSelect.value = '';
    if (elements.adminBatchStatusSelect) elements.adminBatchStatusSelect.value = '';
    if (elements.adminBatchAutoRenewSelect) elements.adminBatchAutoRenewSelect.value = '';
    await reloadAdminDashboard();
  } catch (error) {
    showToast(error?.message || '批量修改失败');
  }
}

async function exportMembersCsv() {
  const query = new URLSearchParams({
    search: String(state?.admin?.query || ''),
    planCode: String(state?.admin?.planCode || 'all'),
    status: String(state?.admin?.status || 'all'),
    page: '1',
    pageSize: '2000'
  });
  try {
    const { blob, disposition } = await requestBlob(`/api/admin/reports/members.csv?${query.toString()}`);
    downloadBlobFile(blob, disposition, 'members.csv');
    showToast('CSV 已下载');
  } catch (error) {
    showToast(error?.message || 'CSV 导出失败');
  }
}

async function updateNoteStatusByRow(rowEl, btn) {
  const noteId = Number(rowEl?.dataset?.noteId || 0);
  const select = rowEl?.querySelector('[data-note-status="1"]');
  const status = String(select?.value || '').toLowerCase();
  if (!noteId || !NOTE_STATUSES.includes(status)) return;

  btn.disabled = true;
  try {
    await requestJson(`/api/admin/notes/${noteId}/status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    showToast('工单状态已更新');
    await reloadAdminDashboard();
  } catch (error) {
    showToast(error?.message || '工单更新失败');
  } finally {
    btn.disabled = false;
  }
}

async function saveGateRuleByRow(rowEl, btn) {
  const planCode = String(rowEl?.dataset?.planCode || '').toLowerCase();
  if (!['free', 'pro', 'max'].includes(planCode)) return;

  const deviceInput = rowEl.querySelector('[data-field="deviceLimit"]');
  const concurrentInput = rowEl.querySelector('[data-field="concurrentLimit"]');
  const scopesInput = rowEl.querySelector('[data-field="scopes"]');

  const payload = {
    deviceLimit: Number(deviceInput?.value || 1),
    concurrentLimit: Number(concurrentInput?.value || 1),
    scopes: normalizeScopesInput(scopesInput?.value)
  };

  btn.disabled = true;
  try {
    await requestJson(`/api/admin/gate/rules/${planCode}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    showToast(`${planCode.toUpperCase()} 门禁规则已更新`);
    await reloadAdminDashboard();
  } catch (error) {
    showToast(error?.message || '规则更新失败');
  } finally {
    btn.disabled = false;
  }
}

async function triggerAutoDowngrade(btn) {
  if (!canMutateMembers()) {
    showToast('当前角色无自动降级权限');
    return;
  }

  btn.disabled = true;
  try {
    const result = await requestJson('/api/admin/alerts/auto-downgrade', { method: 'POST' });
    showToast(`自动降级完成：${Number(result?.downgraded || 0)} 人`);
    await reloadAdminDashboard();
  } catch (error) {
    showToast(error?.message || '自动降级失败');
  } finally {
    btn.disabled = false;
  }
}

async function createNoteForCurrentMember() {
  const userId = Number(state?.admin?.selectedUserId || 0);
  if (!userId) return;
  if (!canOperateNotes()) {
    showToast('当前角色无创建工单权限');
    return;
  }

  const input = elements.adminMemberContext?.querySelector('#adminContextNoteInput');
  const statusSelect = elements.adminMemberContext?.querySelector('#adminContextNoteStatus');
  const note = String(input?.value || '').trim();
  const status = String(statusSelect?.value || 'open').toLowerCase();

  if (!note) {
    showToast('请输入工单内容');
    return;
  }

  try {
    await requestJson(`/api/admin/members/${userId}/notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note, status })
    });
    showToast('工单已创建');
    await reloadMemberContext(userId);
    if (getActiveTab() === 'notes') {
      await reloadAdminDashboard();
    }
  } catch (error) {
    showToast(error?.message || '创建工单失败');
  }
}

async function createApiKeyForCurrentMember() {
  const userId = Number(state?.admin?.selectedUserId || 0);
  if (!userId) return;
  if (!canMutateMembers()) {
    showToast('当前角色无创建密钥权限');
    return;
  }

  const nameInput = elements.adminMemberContext?.querySelector('#adminContextKeyName');
  const scopesInput = elements.adminMemberContext?.querySelector('#adminContextKeyScopes');

  const name = String(nameInput?.value || '').trim() || '默认密钥';
  const scopes = normalizeScopesInput(scopesInput?.value);

  try {
    const payload = await requestJson(`/api/admin/members/${userId}/api-keys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, scopes })
    });
    currentMemberContext.lastToken = String(payload?.token || '');
    showToast('API Key 已创建');
    await reloadMemberContext(userId);
  } catch (error) {
    showToast(error?.message || '创建 API Key 失败');
  }
}

async function revokeApiKeyById(apiKeyId) {
  if (!apiKeyId) return;
  if (!canMutateMembers()) {
    showToast('当前角色无吊销权限');
    return;
  }

  try {
    await requestJson(`/api/admin/api-keys/${apiKeyId}/revoke`, {
      method: 'POST'
    });
    showToast('API Key 已吊销');
    if (state?.admin?.selectedUserId) {
      await reloadMemberContext(state.admin.selectedUserId);
    }
  } catch (error) {
    showToast(error?.message || '吊销失败');
  }
}

async function reloadAdminDashboard() {
  if (!isStaffUser()) {
    renderNoPermissionHint();
    return;
  }

  const seq = ++adminReloadSeq;
  setLoadingState(true);

  try {
    readFiltersFromUi();

    const [overview] = await Promise.all([
      fetchOverview()
    ]);

    if (seq !== adminReloadSeq) return;
    renderOverview(overview);

    const tab = getActiveTab();

    if (tab === 'overview') {
      const [billing, funnel, report] = await Promise.all([
        fetchBillingOverview(),
        fetchFunnel(),
        fetchReportSummary()
      ]);
      if (seq !== adminReloadSeq) return;
      renderBilling(billing);
      renderFunnel(funnel);
      renderReports(report);
      return;
    }

    if (tab === 'members') {
      const members = await fetchMembers();
      if (seq !== adminReloadSeq) return;
      renderMembers(members);
      if (state?.admin?.selectedUserId) {
        await reloadMemberContext(state.admin.selectedUserId);
      }
      return;
    }

    if (tab === 'billing') {
      const billing = await fetchBillingOverview();
      if (seq !== adminReloadSeq) return;
      renderBilling(billing);
      return;
    }

    if (tab === 'funnel') {
      const funnel = await fetchFunnel();
      if (seq !== adminReloadSeq) return;
      renderFunnel(funnel);
      return;
    }

    if (tab === 'risk') {
      const flags = await fetchRiskFlags();
      if (seq !== adminReloadSeq) return;
      renderRisk(flags);
      return;
    }

    if (tab === 'alerts') {
      const alerts = await fetchAlerts();
      if (seq !== adminReloadSeq) return;
      renderAlerts(alerts);
      return;
    }

    if (tab === 'notes') {
      const notes = await fetchNotes();
      if (seq !== adminReloadSeq) return;
      renderNotes(notes);
      return;
    }

    if (tab === 'gate') {
      const rules = await fetchGateRules();
      if (seq !== adminReloadSeq) return;
      renderGateRules(rules);
      return;
    }

    if (tab === 'audit') {
      const logs = await fetchAuditLogs();
      if (seq !== adminReloadSeq) return;
      renderAudit(logs);
      return;
    }

    if (tab === 'reports') {
      const report = await fetchReportSummary();
      if (seq !== adminReloadSeq) return;
      renderReports(report);
    }
  } catch (error) {
    if (seq !== adminReloadSeq) return;
    showToast(error?.message || '管理台数据加载失败');
  } finally {
    if (seq === adminReloadSeq) {
      setLoadingState(false);
    }
  }
}

function bindEvents() {
  elements.adminRefreshBtn?.addEventListener('click', () => {
    reloadAdminDashboard();
  });

  elements.adminExportMembersCsvBtn?.addEventListener('click', () => {
    exportMembersCsv();
  });

  elements.adminPlanFilter?.addEventListener('change', () => {
    if (getActiveTab() !== 'members') return;
    reloadAdminDashboard();
  });

  elements.adminStatusFilter?.addEventListener('change', () => {
    if (getActiveTab() !== 'members') return;
    reloadAdminDashboard();
  });

  elements.adminRangeFilter?.addEventListener('change', () => {
    if (elements.adminReportsRangeFilter) {
      elements.adminReportsRangeFilter.value = String(elements.adminRangeFilter.value || 'monthly');
    }
    reloadAdminDashboard();
  });

  elements.adminSearchInput?.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    reloadAdminDashboard();
  });

  elements.adminTabs?.addEventListener('click', (e) => {
    const btn = e.target instanceof Element ? e.target.closest('[data-admin-tab]') : null;
    if (!btn) return;
    const tab = String(btn.getAttribute('data-admin-tab') || '').toLowerCase();
    if (!TAB_KEYS.includes(tab)) return;
    setActiveTab(tab);
    reloadAdminDashboard();
  });

  elements.adminSelectAllRows?.addEventListener('change', () => {
    if (elements.adminSelectAllRows.checked) {
      selectedUserIds = new Set(currentMembers.map((item) => Number(item?.userId || 0)).filter((id) => id > 0));
    } else {
      selectedUserIds = new Set();
    }
    renderMembers(currentMembers);
  });

  elements.adminBatchApplyBtn?.addEventListener('click', () => {
    applyBatchUpdate();
  });

  elements.adminMembersTableBody?.addEventListener('change', (e) => {
    const check = e.target instanceof Element ? e.target.closest('[data-member-check]') : null;
    if (!check) return;
    const rowEl = check.closest('tr[data-user-id]');
    if (!rowEl) return;
    const userId = Number(rowEl.dataset.userId || 0);
    if (!userId) return;
    if (check.checked) selectedUserIds.add(userId);
    else selectedUserIds.delete(userId);
    updateBatchToolbar();
  });

  elements.adminMembersTableBody?.addEventListener('click', async (e) => {
    const saveBtn = e.target instanceof Element ? e.target.closest('[data-admin-save]') : null;
    if (saveBtn) {
      const rowEl = saveBtn.closest('tr[data-user-id]');
      if (!rowEl) return;
      await saveMemberRow(rowEl, saveBtn);
      return;
    }

    const detailBtn = e.target instanceof Element ? e.target.closest('[data-admin-member-detail]') : null;
    if (detailBtn) {
      const rowEl = detailBtn.closest('tr[data-user-id]');
      const userId = Number(rowEl?.dataset?.userId || 0);
      if (!userId) return;
      try {
        await reloadMemberContext(userId);
      } catch (error) {
        showToast(error?.message || '用户详情加载失败');
      }
    }
  });

  elements.adminMemberContext?.addEventListener('click', async (e) => {
    const createNoteBtn = e.target instanceof Element ? e.target.closest('#adminContextCreateNoteBtn') : null;
    if (createNoteBtn) {
      await createNoteForCurrentMember();
      return;
    }

    const createKeyBtn = e.target instanceof Element ? e.target.closest('#adminContextCreateKeyBtn') : null;
    if (createKeyBtn) {
      await createApiKeyForCurrentMember();
      return;
    }

    const revokeBtn = e.target instanceof Element ? e.target.closest('[data-admin-revoke-key]') : null;
    if (revokeBtn) {
      const wrap = revokeBtn.closest('[data-api-key-id]');
      const apiKeyId = Number(wrap?.dataset?.apiKeyId || 0);
      await revokeApiKeyById(apiKeyId);
    }
  });

  elements.adminAutoDowngradeBtn?.addEventListener('click', (e) => {
    if (!(e.currentTarget instanceof HTMLButtonElement)) return;
    triggerAutoDowngrade(e.currentTarget);
  });

  elements.adminNotesRefreshBtn?.addEventListener('click', () => {
    if (getActiveTab() !== 'notes') return;
    reloadAdminDashboard();
  });

  elements.adminNotesStatusFilter?.addEventListener('change', () => {
    if (getActiveTab() !== 'notes') return;
    reloadAdminDashboard();
  });

  elements.adminNotesTableBody?.addEventListener('click', async (e) => {
    const saveBtn = e.target instanceof Element ? e.target.closest('[data-admin-note-save]') : null;
    if (!saveBtn) return;
    const rowEl = saveBtn.closest('tr[data-note-id]');
    if (!rowEl) return;
    await updateNoteStatusByRow(rowEl, saveBtn);
  });

  elements.adminGateRulesTableBody?.addEventListener('click', async (e) => {
    const saveBtn = e.target instanceof Element ? e.target.closest('[data-admin-gate-save]') : null;
    if (!saveBtn) return;
    const rowEl = saveBtn.closest('tr[data-plan-code]');
    if (!rowEl) return;
    await saveGateRuleByRow(rowEl, saveBtn);
  });

  elements.adminAuditRefreshBtn?.addEventListener('click', () => {
    if (getActiveTab() !== 'audit') return;
    reloadAdminDashboard();
  });

  elements.adminAuditActionFilter?.addEventListener('change', () => {
    if (getActiveTab() !== 'audit') return;
    reloadAdminDashboard();
  });

  elements.adminReportsRefreshBtn?.addEventListener('click', () => {
    if (getActiveTab() !== 'reports') return;
    reloadAdminDashboard();
  });

  elements.adminReportsRangeFilter?.addEventListener('change', () => {
    if (elements.adminRangeFilter) {
      elements.adminRangeFilter.value = String(elements.adminReportsRangeFilter.value || 'monthly');
    }
    if (getActiveTab() !== 'reports') return;
    reloadAdminDashboard();
  });

  if (typeof window !== 'undefined') {
    window.addEventListener('membership:changed', () => {
      if (!isStaffUser() && state.currentView === 'admin') {
        switchView('shelf');
      }
    });
  }
}

export function initAdminDashboardModule(context = {}) {
  bindDeps(context);
  if (!state || !elements) {
    throw new Error('Admin dashboard module requires state and elements');
  }
  if (adminDashboardInitialized) return;
  ensureAdminStateShape();
  syncFiltersToUi();
  setActiveTab(state.admin.activeTab || 'overview');
  bindEvents();
  adminDashboardInitialized = true;
}

export async function renderAdminDashboardView() {
  if (!adminDashboardInitialized) return;
  await reloadAdminDashboard();
}

export function isAdminDashboardModuleInitialized() {
  return adminDashboardInitialized;
}
